import React, { useState, useRef } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../hooks/useTranslation';
import { useAuthStore } from '../store/useAuthStore';
import type { Database } from '../lib/database.types';
import { serviceCategories } from '../pages/ServicesPage';

type Car = Database['public']['Tables']['cars']['Row'];

interface AddServiceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestAdded: () => void;
  approvedCars: Car[];
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 10;
const MAX_SERVICES = 5;

export function AddServiceRequestModal({ isOpen, onClose, onRequestAdded, approvedCars }: AddServiceRequestModalProps) {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedCar, setSelectedCar] = useState<string>('');
  const [description, setDescription] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleServiceToggle = (service: string) => {
    setSelectedServices(prev => {
      if (prev.includes(service)) {
        return prev.filter(s => s !== service);
      } else {
        if (prev.length >= MAX_SERVICES) {
          setError(`You can only select up to ${MAX_SERVICES} services`);
          return prev;
        }
        setError(null);
        return [...prev, service];
      }
    });
  };

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return t.dashboard.vehicles.addNew.errors.fileTooLarge;
    }
    if (!file.type.startsWith('image/')) {
      return t.dashboard.vehicles.addNew.errors.invalidFileType;
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (images.length + files.length > MAX_IMAGES) {
      setError(`You can only upload up to ${MAX_IMAGES} images`);
      return;
    }

    const invalidFiles = files.map(file => validateFile(file)).filter(Boolean);
    if (invalidFiles.length) {
      setError(invalidFiles[0]);
      return;
    }

    setImages(prev => [...prev, ...files]);
    setError(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!selectedCar) {
      setError(t.dashboard.serviceRequests.noVehicles);
      return;
    }
    if (selectedServices.length === 0) {
      setError('Please select at least one service');
      return;
    }
    if (selectedServices.length > MAX_SERVICES) {
      setError(`You can only select up to ${MAX_SERVICES} services`);
      return;
    }
    if (!description) {
      setError('Please provide a description');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, create the service request
      const { data: request, error: requestError } = await supabase
        .from('service_requests')
        .insert({
          profile_id: user.id,
          car_id: selectedCar,
          description: description,
          status: 'pending'
        })
        .select()
        .single();

      if (requestError) throw requestError;
      if (!request) throw new Error('Failed to create service request');

      // Then, fetch all services to get their IDs
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, name')
        .in('name', selectedServices);

      if (servicesError) throw servicesError;
      if (!servicesData) throw new Error('Failed to fetch services');

      // Create service request services entries
      const serviceRequestServices = servicesData.map(service => ({
        service_request_id: request.id,
        service_id: service.id
      }));

      const { error: servicesInsertError } = await supabase
        .from('service_request_services')
        .insert(serviceRequestServices);

      if (servicesInsertError) throw servicesInsertError;

      // Upload images if provided
      if (images.length > 0) {
        const imageUrls = await Promise.all(
          images.map(async (image, index) => {
            const fileExt = image.name.split('.').pop();
            const fileName = `${request.id}-${Date.now()}-${index}.${fileExt}`;
            const filePath = `${request.id}/${fileName}`;

            const { error: uploadError } = await supabase.storage
              .from('service-request-images')
              .upload(filePath, image, {
                upsert: true,
                onUploadProgress: (progress) => {
                  const percentage = (progress.loaded / progress.total) * 100;
                  setUploadProgress(prev => {
                    const newProgress = [...prev];
                    newProgress[index] = percentage;
                    return newProgress;
                  });
                },
              });

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
              .from('service-request-images')
              .getPublicUrl(filePath);

            return publicUrl;
          })
        );

        // Add images to service_request_images
        const { error: imagesError } = await supabase
          .from('service_request_images')
          .insert(
            imageUrls.map(url => ({
              service_request_id: request.id,
              image_url: url
            }))
          );

        if (imagesError) throw imagesError;
      }

      setSuccess(true);
      onRequestAdded();
      
      setTimeout(() => {
        onClose();
        setSelectedCar('');
        setDescription('');
        setSelectedServices([]);
        setImages([]);
        setUploadProgress([]);
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error submitting service request:', err);
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Service Request Submitted
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Your service request has been submitted successfully. We will review it and get back to you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t.dashboard.serviceRequests.newRequest}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Vehicle Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.dashboard.serviceRequests.vehicle}
            </label>
            <select
              value={selectedCar}
              onChange={(e) => setSelectedCar(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">{t.dashboard.serviceRequests.vehicle}</option>
              {approvedCars.map((car) => (
                <option key={car.id} value={car.id}>
                  {car.brand} {car.model} ({car.year})
                </option>
              ))}
            </select>
          </div>

          {/* Services Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.services.title}
              </label>
              <span className="text-sm text-gray-500">
                {selectedServices.length}/{MAX_SERVICES} services selected
              </span>
            </div>
            <div className="space-y-4">
              {Object.entries(serviceCategories).map(([key, category]) => {
                const categoryInfo = t.services.categories[category.key];
                return (
                  <div key={key} className="border dark:border-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                      {categoryInfo.title}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {categoryInfo.services.map((service, index) => (
                        <label 
                          key={service} 
                          className={`flex items-center space-x-2 ${
                            selectedServices.length >= MAX_SERVICES && !selectedServices.includes(service)
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedServices.includes(service)}
                            onChange={() => handleServiceToggle(service)}
                            disabled={selectedServices.length >= MAX_SERVICES && !selectedServices.includes(service)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.dashboard.serviceRequests.description}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Please describe what services you need and any specific requirements..."
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload Images ({images.length}/{MAX_IMAGES})
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md relative">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label
                    htmlFor="image-upload"
                    className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload images</span>
                    <input
                      id="image-upload"
                      ref={fileInputRef}
                      name="image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={handleFileChange}
                      disabled={images.length >= MAX_IMAGES}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  PNG, JPG up to 5MB each
                </p>
              </div>
            </div>

            {/* Image Preview */}
            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {images.map((image, index) => (
                  <div key={index} className="relative">
                    <div className="aspect-w-3 aspect-h-2 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className="object-cover w-full h-full"
                      />
                      {uploadProgress[index] > 0 && uploadProgress[index] < 100 && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              {t.common.cancel}
            </button>
            <button
              type="submit"
              disabled={loading || !selectedCar || selectedServices.length === 0 || !description}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Submitting...' : t.dashboard.serviceRequests.newRequest}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}